// Every auto-rollback watch of the job, newest first, each expandable to the
// watch summary the release tracker holds for its deploy.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  MonitorHeart as WatchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { envRegionLabel, formatNumber } from '../lib/rollbackProfiles';
import {
  envRegionChoices,
  rollbackListKey,
  summarySignalRows,
  thresholdRows,
  versionChange,
} from '../lib/rollbackWatches';
import { getColors } from '../theme/tokens';
import { RollbackWatchItem } from '../../types/DeploymentRecord';
import EmptyState from './EmptyState';
import Help from './Help';
import {
  EnvRegionFilter,
  ListError,
  ListLoading,
  LoadOlder,
  mono,
  TierChip,
  UtcTime,
} from './RollbackListParts';
import { SIGNAL_HELP } from '../copy/rollbackProfileHelp';
import { END_REASON_HELP, MODE_HELP, WATCH_FIELD_HELP as HELP } from '../copy/rollbackWatchHelp';

export interface RollbackWatchesTabProps {
  app: string;
  jobId: string;
  /** The breached chip's target: the job page's Breaches tab. */
  onShowBreaches?: () => void;
}

const RollbackWatchesTab: React.FC<RollbackWatchesTabProps> = ({ app, jobId, onShowBreaches }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { wsStatus, rollbackWatches, fetchRollbackWatches } = useDeploymentData();
  const [envRegion, setEnvRegion] = useState('');

  useEffect(() => {
    if (wsStatus === 'connected' && app && jobId) {
      fetchRollbackWatches(app, jobId, { envRegion: envRegion || undefined });
    }
  }, [wsStatus, app, jobId, envRegion, fetchRollbackWatches]);

  const slice = rollbackWatches.get(rollbackListKey(app, jobId, envRegion));
  const all = rollbackWatches.get(rollbackListKey(app, jobId));
  const choices = useMemo(
    () => envRegionChoices((all?.items || []).map(i => i.env_region), envRegion),
    [all?.items, envRegion]
  );
  const items = slice?.items || [];

  let body: React.ReactNode;
  if (!slice || (slice.loading && !slice.loaded)) {
    body = <ListLoading label="Loading watches…" />;
  } else if (items.length === 0) {
    body = slice.error ? null : (
      <EmptyState
        icon={<WatchIcon />}
        title="No watches recorded yet"
        body="A watch is recorded for every lambda or ECS deploy the auto-rollback watch opened."
      />
    );
  } else {
    body = (
      <>
        <Table size="small" sx={tableSx(c)}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>
                <Help title={HELP.watchOpened}>Watch opened</Help>
              </TableCell>
              <TableCell>Env/region</TableCell>
              <TableCell>
                <Help title={HELP.version}>Version</Help>
              </TableCell>
              <TableCell>
                <Help title={HELP.mode}>Mode</Help>
              </TableCell>
              <TableCell>
                <Help title={HELP.endReason}>End reason</Help>
              </TableCell>
              <TableCell>
                <Help title={HELP.highestTier}>Highest tier</Help>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <WatchRow
                key={`${item.env_region}::${item.deployment_id}::${item.watch_open}`}
                item={item}
                onShowBreaches={onShowBreaches}
              />
            ))}
          </TableBody>
        </Table>
        {slice.nextBefore && (
          <LoadOlder
            loading={slice.loading}
            onClick={() =>
              fetchRollbackWatches(app, jobId, { envRegion: envRegion || undefined, before: slice.nextBefore })
            }
          />
        )}
      </>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: c.inkMuted, mb: 2 }}>
        One row per deploy the watch checked, clean or not: how each signal peaked against its threshold and how
        the watch ended. Expand a row for the detail.
      </Typography>
      {choices.length > 0 && <EnvRegionFilter choices={choices} selected={envRegion} onSelect={setEnvRegion} />}
      <ListError error={slice?.error} />
      {body}
    </Box>
  );
};

const tableSx = (c: ReturnType<typeof getColors>) => ({
  '& th': {
    color: c.inkMuted,
    fontWeight: 600,
    backgroundColor: c.canvasSoft,
  },
});

const WatchRow: React.FC<{ item: RollbackWatchItem; onShowBreaches?: () => void }> = ({ item, onShowBreaches }) => {
  const [open, setOpen] = useState(false);
  const { deploy, summary } = item;
  const mode = summary?.mode || deploy?.mode;
  const endReason = deploy?.end_reason;
  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={open ? 'Hide summary' : 'Show summary'}
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}
          >
            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <UtcTime value={item.watch_open} />
        </TableCell>
        <TableCell>{envRegionLabel(item.env_region)}</TableCell>
        <TableCell sx={mono}>{versionChange(deploy?.previous_version, deploy?.version)}</TableCell>
        <TableCell>{mode ? <Help title={MODE_HELP[mode]}>{mode}</Help> : '—'}</TableCell>
        <TableCell sx={mono}>
          {endReason ? <Help title={END_REASON_HELP[endReason]}>{endReason}</Help> : '—'}
        </TableCell>
        <TableCell>
          <TierChip tier={summary?.highest_tier} />
        </TableCell>
        <TableCell>
          {item.breached && (
            <Chip
              label="breached"
              size="small"
              color="error"
              variant="outlined"
              clickable={!!onShowBreaches}
              onClick={onShowBreaches}
              sx={{ height: 22, fontSize: '0.72rem' }}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <WatchSummaryDetail item={item} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const Field: React.FC<{ label: string; help?: string; children: React.ReactNode }> = ({ label, help, children }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box>
      <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block' }}>
        <Help title={help}>{label}</Help>
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
};

const yesNo = (v?: boolean) => (v === undefined || v === null ? '—' : v ? 'Yes' : 'No');

const WatchSummaryDetail: React.FC<{ item: RollbackWatchItem }> = ({ item }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { summary, breach, deploy } = item;
  const thresholds = thresholdRows(summary);
  const signals = summarySignalRows(summary);
  const label = { variant: 'overline' as const, sx: { color: c.inkMuted, display: 'block', mt: 2, mb: 0.5 } };

  if (!summary) {
    return (
      <Typography variant="body2" sx={{ color: c.inkMuted, py: 1.5 }}>
        No watch summary was recorded for this deploy.
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        <Field label="Opted in" help={HELP.optedIn}>
          {yesNo(summary.opted_in)}
        </Field>
        <Field label="Blocked deploys" help={HELP.blockedDeploys}>
          {yesNo(summary.blocked_deploys)}
        </Field>
        <Field label="Skipped (rate limited)" help={HELP.skippedRateLimited}>
          {formatNumber(summary.skipped_rate_limited)}
        </Field>
        <Field label="Window / poll (s)" help={HELP.window}>
          {formatNumber(summary.window_seconds)} / {formatNumber(summary.poll_seconds)}
        </Field>
        <Field label="Plan hash" help={HELP.planHash}>
          <Box component="span" sx={{ ...mono, overflowWrap: 'anywhere' }}>
            {summary.plan_hash || '—'}
          </Box>
        </Field>
        <Field label="Profile version" help={HELP.profileVersion}>
          {summary.profile_version ? `v${summary.profile_version}` : '—'}
        </Field>
        {deploy?.deployment_id && (
          <Field label="Deployment">
            <Box component="span" sx={{ ...mono, overflowWrap: 'anywhere' }}>
              {deploy.deployment_id}
            </Box>
          </Field>
        )}
        {deploy?.watch_end && (
          <Field label="Watch ended">
            <UtcTime value={deploy.watch_end} />
          </Field>
        )}
      </Box>

      {breach && (
        <>
          <Typography {...label}>
            <Help title={HELP.breach}>Breach</Help>
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={mono}>
              {breach.signal || '—'}
            </Box>
            {breach.metric ? ` (${breach.metric})` : ''}: {formatNumber(breach.observed_value)}{' '}
            {breach.comparison || ''} {formatNumber(breach.threshold)}, sample {formatNumber(breach.sample)}, at{' '}
            <UtcTime value={breach.at} />
          </Typography>
        </>
      )}

      {thresholds.length > 0 && (
        <>
          <Typography {...label}>
            <Help title={HELP.thresholds}>Thresholds</Help>
          </Typography>
          <Table size="small" sx={{ maxWidth: 420, ...tableSx(c) }}>
            <TableBody>
              {thresholds.map(t => (
                <TableRow key={t.name}>
                  <TableCell sx={mono}>{t.name}</TableCell>
                  <TableCell align="right">{formatNumber(t.value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Typography {...label}>
        <Help title={HELP.signals}>Signals</Help>
      </Typography>
      {signals.length > 0 ? (
        <Table size="small" sx={tableSx(c)}>
          <TableHead>
            <TableRow>
              <TableCell>Signal</TableCell>
              <TableCell align="right">
                <Help title={HELP.polls}>Polls</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.skipped}>Skipped</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.peak}>Peak</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.peakSustained}>Peak sustained</Help>
              </TableCell>
              <TableCell align="right">
                <Help title={HELP.sample}>Sample min / max</Help>
              </TableCell>
              <TableCell>Breached</TableCell>
              <TableCell>
                <Help title={HELP.breachAt}>Breach at</Help>
              </TableCell>
              <TableCell>
                <Help title={HELP.recoveredAt}>Recovered at</Help>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {signals.map(s => (
              <TableRow key={s.signal}>
                <TableCell sx={mono}>
                  <Help title={SIGNAL_HELP[s.signal]}>{s.signal}</Help>
                </TableCell>
                <TableCell align="right">{formatNumber(s.polls)}</TableCell>
                <TableCell align="right">{formatNumber(s.skipped)}</TableCell>
                <TableCell align="right">{formatNumber(s.peak)}</TableCell>
                <TableCell align="right">{formatNumber(s.peak_sustained)}</TableCell>
                <TableCell align="right">
                  {formatNumber(s.min_sample)} / {formatNumber(s.max_sample)}
                </TableCell>
                <TableCell>{yesNo(s.breached)}</TableCell>
                <TableCell>
                  <UtcTime value={s.breach_at} />
                </TableCell>
                <TableCell>
                  <UtcTime value={s.recovered_at} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" sx={{ color: c.inkMuted }}>
          No signals.
        </Typography>
      )}
    </Box>
  );
};

export default RollbackWatchesTab;
